var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button as MuiButton } from '@material-ui/core';
import clsx from 'clsx';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { acidForwardRef } from '../../utilities/forwardRef';
var defaultStyles = makeStyles(function (theme) {
    var _a, _b;
    return ({
        root: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            border: "1px solid ".concat(theme.palette.divider),
            padding: '0px 1.25rem',
            borderRadius: '2px',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        primary: {
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
            },
        },
        secondary: {
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
            },
        },
        default: {
            '&:hover': {
                backgroundColor: (_a = theme.palette.default) === null || _a === void 0 ? void 0 : _a.main,
                color: (_b = theme.palette.default) === null || _b === void 0 ? void 0 : _b.contrastText,
            },
        },
        success: {
            '&:hover': {
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.contrastText,
            },
        },
        warning: {
            '&:hover': {
                backgroundColor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
            },
        },
        info: {
            '&:hover': {
                backgroundColor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
            },
        },
        danger: {
            '&:hover': {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
            },
        },
        inherit: {
            '&:hover': {
                backgroundColor: 'inherit',
                color: 'inherit',
            },
        },
        asLink: {
            background: 'none',
            border: 'none',
            padding: '0',
            font: 'inherit',
            cursor: 'pointer',
            outline: 'inherit',
            textTransform: 'none',
            color: theme.palette.primary.dark,
            '&:hover': {
                background: 'none !important',
                textDecoration: 'none',
                color: theme.palette.primary.main,
            },
        },
    });
});
var containedStyles = makeStyles(function (theme) {
    var _a, _b, _c;
    return ({
        default: {
            backgroundColor: (_a = theme.palette.default) === null || _a === void 0 ? void 0 : _a.main,
            color: (_b = theme.palette.default) === null || _b === void 0 ? void 0 : _b.contrastText,
            '&:hover': {
                backgroundColor: (_c = theme.palette.default) === null || _c === void 0 ? void 0 : _c.dark,
            },
        },
        primary: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
        secondary: {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
            },
        },
        success: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.success.dark,
            },
        },
        info: {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.info.dark,
            },
        },
        warning: {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.warning.dark,
            },
        },
        danger: {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.error.dark,
            },
        },
    });
});
var outlinedStyles = makeStyles(function (theme) {
    var _a, _b, _c, _d;
    return ({
        default: {
            color: (_a = theme.palette.default) === null || _a === void 0 ? void 0 : _a.main,
            borderColor: (_b = theme.palette.default) === null || _b === void 0 ? void 0 : _b.main,
            '&:hover': {
                backgroundColor: (_c = theme.palette.default) === null || _c === void 0 ? void 0 : _c.tint,
                color: (_d = theme.palette.default) === null || _d === void 0 ? void 0 : _d.dark,
            },
        },
        primary: {
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: fade(theme.palette.primary.main, 0.2),
                color: theme.palette.primary.dark,
            },
        },
        secondary: {
            color: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.main,
            '&:hover': {
                backgroundColor: theme.palette.secondary.tint,
                color: theme.palette.secondary.dark,
            },
        },
        success: {
            color: theme.palette.success.main,
            borderColor: theme.palette.success.main,
            '&:hover': {
                backgroundColor: theme.palette.success.tint,
                color: theme.palette.success.dark,
            },
        },
        info: {
            color: theme.palette.info.main,
            borderColor: theme.palette.info.main,
            '&:hover': {
                backgroundColor: theme.palette.info.tint,
                color: theme.palette.info.dark,
            },
        },
        warning: {
            color: theme.palette.warning.main,
            borderColor: theme.palette.warning.main,
            '&:hover': {
                backgroundColor: theme.palette.warning.tint,
                color: theme.palette.warning.dark,
            },
        },
        danger: {
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            '&:hover': {
                backgroundColor: theme.palette.error.tint,
                color: theme.palette.error.dark,
            },
        },
    });
});
var Button = acidForwardRef(function (props, ref) {
    var _a;
    var _b = props.variant, variant = _b === void 0 ? 'default' : _b, _c = props.color, color = _c === void 0 ? 'default' : _c, className = props.className, children = props.children, asLink = props.asLink;
    var defaultStyle = defaultStyles(props);
    var containedStyle = containedStyles(props);
    var outlinedStyle = outlinedStyles(props);
    if (variant !== 'default') {
        switch (color) {
            case 'default':
            case 'success':
            case 'info':
            case 'warning':
            case 'danger':
            case 'primary':
            case 'secondary':
                return (React.createElement(MuiButton, __assign({}, props, { color: undefined, variant: variant, ref: ref, classes: {
                        root: clsx(className),
                        contained: clsx(containedStyle[String(color)
                            .toLowerCase()]),
                        outlined: clsx(outlinedStyle[String(color)
                            .toLowerCase()]),
                        text: clsx(outlinedStyle[String(color)
                            .toLowerCase()]),
                    } }), children));
            default:
                return (React.createElement(MuiButton, __assign({}, props, { color: color, variant: variant, ref: ref }), children));
        }
    }
    return (React.createElement(MuiButton, __assign({}, props, { color: "inherit", variant: "text", className: clsx(defaultStyle.root, defaultStyle[color || 'default'], className, (_a = {}, _a[defaultStyle.asLink] = asLink, _a)), ref: ref }), children));
});
export default Button;
Button.defaultProps = {
    variant: 'default',
};
//# sourceMappingURL=Button.js.map